import { AsyncValidatorFn, ValidatorFn } from '@angular/forms';
import { XVII18nService } from '@app/i18n/services';
import { get, has, includes } from 'lodash';

import { ValidationService } from '../validation/validation.service';
import { IFieldPreferences } from './schema-form-interfaces';

/**
 * get date time max/min validator
 *
 * @param field target field
 * @param validationService validation service(contains all validator)
 * @returns date time max/min validator
 */
const getDateTimeFormatValidator = (
  field: IFieldPreferences,
  validationService: ValidationService,
): ValidatorFn => {
  // automatically add minDate and maxDate validators when applicable
  if (get(field, 'minimum', false) !== false) {
    return validationService.minDateValidator(field.minimum);
  }
  if (get(field, 'maximum', false) !== false) {
    return validationService.maxDateValidator(field.maximum);
  }
};

/**
 *  returns array of angular validators
 * that corresponds the schema node requirements
 *
 * @param field - combined field preferences with
 * premerged 'required' schema key
 * @returns Function[] - array of validators
 */
// eslint-disable-next-line complexity
export const createSchemaValidators: Function = (
  field: IFieldPreferences,
  options: {
    i18nService: XVII18nService;
  },
): ValidatorFn[] => {
  const validationService: ValidationService = new ValidationService(
    options.i18nService,
  );
  const validators: ValidatorFn[] = get(field, 'validators', []);
  if (
    field.required &&
    !includes(validators, validationService.requiredValidator)
  ) {
    if (includes(['string', 'array'], field.type)) {
      validators.push(validationService.valueType(field.type));
    } else {
      validators.push(validationService.requiredValidator);
    }
  }

  if (field.type === 'array') {
    if (has(field, 'minItems')) {
      validators.push(validationService.minArrayLength(field.minItems));
    }
    if (has(field, 'maxItems')) {
      validators.push(validationService.maxArrayLength(field.maxItems));
    }
  }

  if (field.type === 'string') {
    if (field.format) {
      const urlValidator = validationService.urlValidator();
      const fieldFormatValidatorMapping: Record<string, ValidatorFn> = {
        email: validationService.emailValidator,
        uri: urlValidator,
        'uri-optional': urlValidator,
        'date-time': getDateTimeFormatValidator(field, validationService),
      };

      if (has(fieldFormatValidatorMapping, field.format)) {
        // add here validators for FORMAT property from fieldFormatValidatorMapping
        validators.push(fieldFormatValidatorMapping[field.format]);
      }
    }
    if (get(field, 'pattern', false) !== false) {
      validators.push(validationService.patternValidator(field.pattern));
    }
    if (field.minLength) {
      // angular fix. minLength does not give any errors for an empty string
      if (!validators.includes(validationService.requiredValidator)) {
        validators.push(validationService.requiredValidator);
      }

      if (field.trim) {
        validators.push(validationService.trimmedMinLength(field.minLength));
      } else {
        validators.push(validationService.minLength(field.minLength));
      }
    }
    if (has(field, 'maxLength')) {
      validators.push(validationService.maxLength(field.maxLength));
    }
  }

  if (includes(['number', 'integer'], field.type)) {
    if (get(field, 'minimum', false) !== false) {
      validators.push(validationService.minValidator(field.minimum));
    }
    if (get(field, 'exclusiveMinimum', false) !== false) {
      validators.push(
        validationService.strictMinValidator(field.exclusiveMinimum),
      );
    }
    if (get(field, 'maximum', false) !== false) {
      validators.push(validationService.maxValidator(field.maximum));
    }
    if (get(field, 'exclusiveMaximum', false) !== false) {
      validators.push(
        validationService.strictMaxValidator(field.exclusiveMaximum),
      );
    }
    if (has(field, 'multipleOf')) {
      validators.push(
        validationService.numberMultipleOfValidator(field.multipleOf),
      );
    }

    if (field.type === 'number') {
      validators.push(validationService.numberValidator());
    }

    if (field.type === 'integer' && !has(field, 'multipleOf')) {
      validators.push(validationService.numberMultipleOfValidator(1));
    }
  }

  return validators;
};

export const createSchemaAsyncValidators: Function = (
  field: IFieldPreferences,
): AsyncValidatorFn[] => {
  const asyncValidators: AsyncValidatorFn[] = [];
  if (field.asyncValidators) {
    asyncValidators.push(...field.asyncValidators);
  }

  return asyncValidators;
};
